import { useState } from "react";
import strappedConnected from "modules/Core/higher-order-components/strappedConnected";
import auth from "modules/auth/state";
import { Button, Container, Features, Input, Text } from "modules/Core/sub-modules/ui-kit/exports";
import useOnError from "modules/Core/sub-modules/Dialog/hooks/useOnError";
import useOnSuccess from "modules/Core/sub-modules/Dialog/hooks/useOnSuccess";
import {
    hasPasswordProvider as firebaseHasPasswordProvider,
    linkEmailPassword,
} from "modules/auth/utils";
import styles from "./styles.module.scss";

const AccountTab = ({
    email,
    username,
    hasPassword,
    onEnablePassword,
    password,
    confirmPassword,
    setPassword,
    setConfirmPassword,
    isLinking,
}) => (
    <Container className={styles.account} flex col>
        <Features
            row
            features={[
                { name: "Email", content: email || "Not set" },
                { name: "Username", content: username || "Not set" },
            ]}
        />

        <Container className={styles.passwordBlock} flex col>
            {hasPassword ? (
                <>
                    <Text className={styles.passwordTitle}>Email and password sign-in</Text>
                    <Text className={styles.passwordCopy}>
                        This account can already sign in with email and password, including on mobile.
                    </Text>
                </>
            ) : (
                <>
                    <Text className={styles.passwordTitle}>Enable email and password</Text>
                    <Text className={styles.passwordCopy}>
                        Google sign-in is not available on mobile yet. Set a password for{" "}
                        <b>{email || "this account"}</b> so you can sign in there with email.
                    </Text>
                    <Input
                        label="Password"
                        type="password"
                        value={password}
                        onChange={setPassword}
                    />
                    <Input
                        label="Confirm password"
                        type="password"
                        value={confirmPassword}
                        onChange={setConfirmPassword}
                    />
                    <Container flex flexEnd>
                        <Button onClick={onEnablePassword} loading={isLinking}>
                            Enable email sign-in
                        </Button>
                    </Container>
                </>
            )}
        </Container>
    </Container>
);

export default strappedConnected(
    AccountTab,
    {
        email: auth.validateToken.selectors.email,
        username: auth.validateToken.selectors.name,
        hasPasswordProvider: auth.validateToken.selectors.hasPasswordProvider,
    },
    {
        refetch: auth.validateToken.action,
    },
    ({ email, username, hasPasswordProvider, refetch }) => {
        const [password, setPassword] = useState("");
        const [confirmPassword, setConfirmPassword] = useState("");
        const [isLinking, setIsLinking] = useState(false);
        const onError = useOnError();
        const onSuccess = useOnSuccess();
        const hasPassword = hasPasswordProvider || firebaseHasPasswordProvider();

        const onEnablePassword = async () => {
            if (!password || password.length < 6) {
                onError("Password must be at least 6 characters.");
                return;
            }
            if (password !== confirmPassword) {
                onError("Passwords do not match.");
                return;
            }

            setIsLinking(true);
            try {
                await linkEmailPassword(email, password);
                setPassword("");
                setConfirmPassword("");
                onSuccess("Email and password sign-in is enabled. You can now log in on mobile.");
                refetch();
            } catch (error) {
                onError(error.message);
            } finally {
                setIsLinking(false);
            }
        };

        return {
            email,
            username,
            hasPassword,
            password,
            confirmPassword,
            setPassword,
            setConfirmPassword,
            isLinking,
            onEnablePassword,
        };
    }
);
